<template>
  <header
    class="page-title-bar"
    :class="`page-title-bar--${variant}`"
    :aria-labelledby="titleId"
  >
    <span class="page-title-bar__icon" aria-hidden="true">
      <img v-if="iconSrc" :src="iconSrc" alt="" />
      <i v-else :class="iconClass"></i>
    </span>

    <div class="page-title-bar__copy">
      <h1 :id="titleId" class="page-title-bar__title">{{ title }}</h1>
      <p class="page-title-bar__subtitle">{{ subtitle }}</p>
    </div>

    <img
      v-if="illustrationSrc"
      class="page-title-bar__illustration"
      :src="illustrationSrc"
      alt=""
      aria-hidden="true"
    />
  </header>
</template>

<script>
export default {
  name: "PageTitleBar",
  props: {
    titleId: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      required: true,
    },
    iconClass: {
      type: String,
      default: "fa-solid fa-circle-info",
    },
    iconSrc: {
      type: String,
      default: "",
    },
    illustrationSrc: {
      type: String,
      default: "",
    },
    variant: {
      type: String,
      default: "default",
    },
  },
};
</script>

<style scoped>
.page-title-bar {
  position: relative;
  display: flex;
  width: 100%;
  min-height: 5.85rem;
  padding: 0.72rem 13rem 0.72rem 1rem;
  align-items: center;
  gap: 0.9rem;
  overflow: hidden;
  box-sizing: border-box;
  border: 1px solid rgba(20, 118, 94, 0.1);
  border-radius: 12px;
  background: linear-gradient(105deg, #f5fbf8 0%, #eef8f3 68%, #e9f5ef 100%);
  box-shadow: 0 7px 20px rgba(33, 105, 81, 0.055);
}

.page-title-bar__icon {
  position: relative;
  z-index: 1;
  display: inline-flex;
  width: 3.7rem;
  height: 3.7rem;
  flex: 0 0 3.7rem;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: linear-gradient(145deg, #e4f7ec, #d8f1e3);
  color: #08784f;
  font-size: 1.65rem;
}

.page-title-bar__icon img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.page-title-bar__copy {
  position: relative;
  z-index: 1;
  min-width: 0;
  text-align: left;
}

.page-title-bar__title {
  margin: 0;
  color: #15334b;
  font-size: 1.42rem;
  font-weight: 750;
  letter-spacing: -0.015em;
  line-height: 1.15;
}

.page-title-bar__subtitle {
  margin: 0.3rem 0 0;
  color: #657c88;
  font-size: 0.82rem;
  line-height: 1.35;
}

.page-title-bar__illustration {
  position: absolute;
  z-index: 0;
  right: 1.15rem;
  bottom: -2.35rem;
  display: block;
  width: 11.5rem;
  max-height: 10rem;
  object-fit: contain;
  opacity: 0.58;
  pointer-events: none;
  user-select: none;
}

.page-title-bar--store {
  border-color: rgba(102, 123, 140, 0.14);
  background: linear-gradient(105deg, #f7f9fa 0%, #eff3f6 68%, #e7edf2 100%);
  box-shadow: 0 7px 20px rgba(64, 83, 99, 0.06);
}

.page-title-bar--store .page-title-bar__icon {
  background: linear-gradient(145deg, #eef2f5, #dce5ec);
  color: #53697a;
}

.page-title-bar--store .page-title-bar__illustration {
  bottom: -1.45rem;
  width: 12rem;
}

.page-title-bar--settings {
  border-color: rgba(102, 123, 140, 0.14);
  background: linear-gradient(105deg, #f7f9fa 0%, #eff3f6 68%, #e7edf2 100%);
  box-shadow: 0 7px 20px rgba(64, 83, 99, 0.06);
}

.page-title-bar--settings .page-title-bar__icon {
  background: linear-gradient(145deg, #eef2f5, #dce5ec);
  color: #53697a;
}

.page-title-bar--settings .page-title-bar__illustration {
  bottom: -2.15rem;
  width: 11rem;
}

.page-title-bar--info {
  border-color: rgba(102, 123, 140, 0.14);
  background: linear-gradient(105deg, #f7f9fa 0%, #eff3f6 68%, #e7edf2 100%);
  box-shadow: 0 7px 20px rgba(64, 83, 99, 0.06);
}

.page-title-bar--info .page-title-bar__icon {
  background: linear-gradient(145deg, #eef3f7, #e1eaf0);
  color: #49677f;
}

.page-title-bar--info .page-title-bar__illustration {
  bottom: -2.1rem;
  width: 10.6rem;
}

@media (min-width: 981px) and (max-height: 900px) {
  .page-title-bar {
    min-height: 4.15rem;
    padding: 0.38rem 10rem 0.38rem 0.75rem;
    gap: 0.7rem;
  }

  .page-title-bar__icon {
    width: 2.9rem;
    height: 2.9rem;
    flex-basis: 2.9rem;
    font-size: 1.3rem;
  }

  .page-title-bar__title {
    font-size: 1.2rem;
  }

  .page-title-bar__subtitle {
    margin-top: 0.16rem;
    font-size: 0.74rem;
  }

  .page-title-bar__illustration,
  .page-title-bar--store .page-title-bar__illustration,
  .page-title-bar--settings .page-title-bar__illustration {
    right: 0.9rem;
    bottom: -2.25rem;
    width: 9rem;
  }

  .page-title-bar--info .page-title-bar__illustration {
    right: 1rem;
    bottom: -1.9rem;
    width: 8.3rem;
  }

  .page-title-bar--store .page-title-bar__illustration {
    bottom: -1.15rem;
  }
}

@media (max-width: 720px) {
  .page-title-bar {
    min-height: 5.2rem;
    padding-right: 1rem;
  }

  .page-title-bar__illustration {
    display: none;
  }
}
</style>

<style>
.dm .page-title-bar {
  border-color: rgba(147, 195, 176, 0.14);
  background: linear-gradient(105deg, #1e302d 0%, #1b2c29 68%, #192825 100%);
  box-shadow: 0 7px 20px rgba(0, 0, 0, 0.16);
}

.dm .page-title-bar__title {
  color: #edf6f9;
}

.dm .page-title-bar__subtitle {
  color: #abc0c4;
}
</style>
